'use client';

import React, { useMemo, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useInView } from 'react-intersection-observer';
import { motion, AnimatePresence, useReducedMotion, Variants } from 'framer-motion';

import { cn } from '@/lib/utils';
import { useScrollSpyNav } from '@/hooks/useScrollSpyNav';
import { Button, Heading } from '@/components/ui/design-system';
import type { Application, Industry } from './ProductApplications';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 15 } },
};

export function ProductApplicationsClient({ industries }: { industries: Industry[] }) {
  const industrySlugs = useMemo(() => industries.map(i => i.slug), [industries]);
  const {
    activeId: activeIndustry,
    sectionRefs,
    handleItemClick: handleIndustryChange,
    handleSectionInView,
  } = useScrollSpyNav(industrySlugs, industries[0]?.slug);

  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="relative py-24 sm:py-32">
      {/* Background Image with Fixed Attachment (mirrors CaseStudiesCarousel) */}
      <div 
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: 'url(/images/pages/industries/applications/application-bg.png)',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      />
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" aria-hidden="true" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Heading level={2} className="text-4xl !leading-tight mb-4">
            Versatile Applications, Unmatched Quality
          </Heading>
          <p className="text-xl text-muted-foreground">
            Our polymers are at the heart of innovation across diverse industries, enabling performance and reliability where it matters most.
          </p>
        </div>
      </div>

      <div className="lg:hidden container mx-auto px-5 lg:px-8 sm:px-6" style={{ position: 'sticky', top: '72px', zIndex: 50 }}>
        <div className="mb-8">
          <div className="relative mx-auto max-w-full overflow-hidden rounded-2xl border border-white/20 bg-white/50 p-2 shadow-md backdrop-blur-lg dark:border-white/10 dark:bg-neutral-950/60">
            <IndustryNav industries={industries} activeIndustry={activeIndustry} layout="horizontal" onIndustryChange={handleIndustryChange} />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-16 items-start">
          <div className="hidden lg:block lg:self-start" style={{ position: 'sticky', top: '96px' }}>
            <IndustryNav industries={industries} activeIndustry={activeIndustry} layout="vertical" onIndustryChange={handleIndustryChange} />
          </div>

          <div className="lg:col-span-2 relative">
            <ApplicationList 
              industries={industries} 
              onIndustryInView={handleSectionInView}
              sectionRefs={sectionRefs}
            />
          </div>
        </div>

        <div className="text-center mt-20">
          <Button asChild size="xl" className="rounded-full">
            <Link href="/industries">Explore All Applications</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function IndustryNav({ industries, activeIndustry, layout = 'vertical', onIndustryChange }: { industries: Industry[]; activeIndustry: string; layout?: 'vertical' | 'horizontal'; onIndustryChange?: (slug: string) => void }) {
  const commonButtonClasses = 'transition-all duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transform';
  
  if (layout === 'horizontal') {
    return (
      <div className="relative">
        <div className="flex items-center justify-center space-x-2 overflow-x-auto scrollbar-hide">
          {industries.map(industry => (
            <button
              key={industry.slug}
              onClick={() => onIndustryChange?.(industry.slug)}
              className={cn(
                'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-950 hover:scale-105',
                commonButtonClasses,
                activeIndustry === industry.slug
                  ? 'premium-gradient-primary text-white shadow-lg scale-105'
                  : 'bg-white/80 dark:bg-neutral-900/60 text-neutral-700 dark:text-neutral-300 hover:bg-white hover:shadow-md dark:hover:bg-neutral-800/80 dark:hover:text-white',
              )}
            >
              {industry.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white dark:bg-neutral-800/50 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 shadow-lg">
      <Heading level={4} className="mb-6 text-lg font-semibold text-neutral-800 dark:text-neutral-200">
        Industries
      </Heading>
      <ul className="space-y-3">
        {industries.map(industry => (
          <li key={industry.slug}>
            <button
              onClick={() => onIndustryChange?.(industry.slug)}
              className={cn(
                'w-full text-left block px-4 py-3 rounded-lg text-lg hover:translate-x-2',
                commonButtonClasses,
                activeIndustry === industry.slug
                  ? 'premium-gradient-primary text-white shadow-lg translate-x-2'
                  : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 hover:shadow-md dark:hover:bg-neutral-700/60 dark:hover:text-white'
              )}
            >
              {industry.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApplicationList({ 
  industries, 
  onIndustryInView,
  sectionRefs
}: { 
  industries: Industry[]; 
  onIndustryInView: (slug: string) => void; 
  sectionRefs: React.MutableRefObject<Map<string, HTMLElement | null>>;
}) {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <div className="space-y-24">
      <AnimatePresence mode="wait">
        {industries.map(industry => (
          <IndustrySection 
            key={industry.slug} 
            industry={industry} 
            onInViewChange={onIndustryInView}
            shouldReduceMotion={shouldReduceMotion}
            sectionRefs={sectionRefs}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

function IndustrySection({ 
  industry, 
  onInViewChange,
  shouldReduceMotion,
  sectionRefs
}: { 
  industry: Industry;
  onInViewChange: (slug: string) => void;
  shouldReduceMotion?: boolean | null;
  sectionRefs: React.MutableRefObject<Map<string, HTMLElement | null>>;
}) {
  const { ref, inView } = useInView({ threshold: 0.4 });

  useEffect(() => {
    if (inView) {
      onInViewChange(industry.slug);
    }
  }, [inView, industry.slug, onInViewChange]);

  return (
    <motion.section 
      id={industry.slug} 
      tabIndex={-1} // Make section focusable
      ref={(el) => {
        ref(el);
        sectionRefs.current.set(industry.slug, el);
      }}
      className="scroll-mt-40 lg:scroll-mt-32 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-background dark:focus-visible:ring-offset-neutral-900 rounded-md"
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, y: -20 }}
      variants={shouldReduceMotion ? undefined : containerVariants}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
      <div className="relative mb-12">
        <Heading level={3} style="gradient" className="text-4xl">
          {industry.name}
        </Heading>
        <div className="absolute -bottom-4 left-0 w-24 h-1 premium-gradient-primary rounded-full" />
      </div>
      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-2 gap-8"
        variants={shouldReduceMotion ? undefined : containerVariants}
      >
        {industry.applications.map((app, index) => (
          <ApplicationCard 
            key={app.id} 
            application={app} 
            index={index} 
          />
        ))}
      </motion.div>
    </motion.section>
  );
}

function ApplicationCard({ application, index }: { application: Application; index: number }) {
  return (
    <motion.div
      variants={itemVariants}
      className="group overflow-hidden rounded-xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl dark:bg-neutral-900 dark:shadow-neutral-900/20 dark:hover:shadow-neutral-900/40 cursor-pointer hover:-translate-y-2"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <Image
          src={application.image}
          alt={application.name}
          fill
          className="object-cover transition-all duration-500 ease-in-out group-hover:scale-110"
          sizes="(max-width: 640px) 100vw, 50vw"
          quality={80}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <div className="p-4 text-center">
        <Heading
          level={4}
          className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 group-hover:text-primary dark:group-hover:text-primary-light transition-colors duration-300"
        >
          {application.name}
        </Heading>
      </div>
    </motion.div>
  );
}
